import classNames from 'classnames'
import { FunctionComponent } from 'react'
import { Icon } from '../../../components'
import { CreatorInterests } from '../../../components/CreatorInterests'
import { VideoPlayer } from '../../../components/VideoPlayer'
import { Avatar } from '../../../elements'
import { Creator } from '../../../gql/types'
import {
  childAgeRangeLocalizedString,
  petLocalizedString,
} from '../../../utils/GqlStrings'
import { formatLongMessage } from '../../../utils/helpers'
import {
  mapAgeGroupToText,
  mapCountryCodeToFlagAndText,
  mapPetToIcon,
  mapChildToIcon,
} from '../../../utils/mappers'

interface IProps {
  creator: Creator
}

const ProfileCard: FunctionComponent<IProps> = ({ creator }) => {
  return (
    <div className='bg-white shadow sm:overflow-hidden rounded-lg'>
      <div className='border-t border-gray-200 px-4 py-5 sm:p-0'>
        <dl className='sm:divide-y sm:divide-gray-200'>
          {creator.occupation && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>Occupation</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {creator.occupation}
              </dd>
            </div>
          )}
          {creator.bio && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>About</dt>
              <dd
                className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'
                dangerouslySetInnerHTML={{
                  __html: formatLongMessage(creator.bio),
                }}
              ></dd>
            </div>
          )}
          {creator.interests && creator.interests.length > 0 && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>Interests</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 space-x-1'>
                <div className='mt-1.5 sm:items-end flex-wrap'>
                  {creator.interests?.map((interest, index) => (
                    <CreatorInterests key={index} interest={interest} />
                  ))}
                </div>
              </dd>
            </div>
          )}

          {creator?.age && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>Age</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {creator.age} years
              </dd>
            </div>
          )}

          {creator?.childAgeRanges && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Children's Ages
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {creator.childAgeRanges.map((childAgeRange, index) => (
                  <span key={index} className='inline-flex items-center mr-2'>
                    {childAgeRangeLocalizedString(childAgeRange)}
                    {mapChildToIcon(childAgeRange) && (
                      <Icon
                        type={mapChildToIcon(childAgeRange)}
                        className='ml-1 w-4 h-4'
                        aria-hidden='true'
                      />
                    )}
                    {index < creator.childAgeRanges.length - 1 && ', '}
                  </span>
                ))}
              </dd>
            </div>
          )}

          {creator?.petsOwner && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>Pets</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {creator.petsOwner.map((petOwner, index) => (
                  <span key={index} className='inline-flex items-center mr-2'>
                    {petLocalizedString(petOwner)}
                    {mapPetToIcon(petOwner) && (
                      <Icon
                        type={mapPetToIcon(petOwner)}
                        className='ml-1 w-4 h-4'
                        aria-hidden='true'
                      />
                    )}
                    {index < creator.petsOwner.length - 1 && ', '}
                  </span>
                ))}
              </dd>
            </div>
          )}
          {creator.addressCity && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>City</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {creator.addressCity}
              </dd>
            </div>
          )}
          {creator.addressCounty && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>County</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {creator.addressCounty}
              </dd>
            </div>
          )}
          {creator.addressState && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>State</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {creator.addressState}
              </dd>
            </div>
          )}
          {creator.countryCode && (
            <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>Country</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {mapCountryCodeToFlagAndText(creator.countryCode)}
              </dd>
            </div>
          )}
          <div className='py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
            <dt className='text-sm font-medium text-gray-500'>
              Introduction Video
            </dt>
            <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              <div className='grid grid-cols-2 gap-x-6 gap-y-6 @md:grid-cols-3 @lg:grid-cols-4'>
                <div className='relative isolate flex flex-col justify-end shadow-sm shadow-black/20 dark:shadow-white/20 overflow-hidden rounded-2xl bg-gray-900 aspect-[0.7]'>
                  <VideoPlayer
                    src={creator.introVideo?.url}
                    poster={creator.thumbnail?.url}
                    className='absolute inset-0 -z-10 h-full w-full'
                    controls
                  />
                </div>
              </div>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}

export { ProfileCard }
